import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { press } from "@/data/portfolio";

export default function Press() {
  useEffect(() => window.scrollTo(0, 0), []);

  return (
    <main className="bg-cream text-ink no-x pt-24" data-testid="press-page">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 mb-14">
        <Link
          to="/"
          className="inline-flex items-center gap-2 label text-ink2 editorial-link"
          data-testid="press-back"
          data-cursor="hover"
        >
          <ArrowLeft size={14} /> Back to studio
        </Link>
      </div>

      <section className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10 mb-24 md:mb-32">
        <div className="col-span-12 md:col-span-3 chapter-num">
          Press · Speaking · Recognition
        </div>
        <div className="col-span-12 md:col-span-8">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
            className="font-display text-[14vw] md:text-[9vw] lg:text-[8vw] leading-[0.9] tracking-tight text-ink"
          >
            In the <em className="italic text-terracotta">press</em>.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.9 }}
            className="mt-6 font-sans font-light text-xl md:text-2xl text-ink2 max-w-[52ch]"
          >
            A TEDx stage and an entrepreneur award, collected here for
            future collaborators and anyone writing about the studio.
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 md:px-10 pb-24 md:pb-32">
        <div className="border-t border-ink">
          {press.map((p, i) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 0.9, delay: i * 0.05 }}
              className="grid grid-cols-12 gap-6 md:gap-10 py-10 md:py-14 border-b border-ink/15 group cursor-pointer hover:bg-ink hover:text-cream transition-colors duration-500"
              data-testid={`press-row-${p.id}`}
              data-cursor="view"
            >
              <div className="col-span-4 md:col-span-1 chapter-num self-center group-hover:text-cream/60">
                0{i + 1}
              </div>
              <div className="col-span-8 md:col-span-2 self-center label group-hover:text-cream/60">
                {p.kind} · {p.year}
              </div>
              <div className="col-span-12 md:col-span-6 self-center">
                <h2 className="font-display text-3xl md:text-5xl leading-[1] tracking-tight">
                  {p.title}
                </h2>
                <p className="mt-3 font-sans text-sm md:text-base text-ink2 group-hover:text-cream/80 max-w-[70ch]">
                  {p.excerpt}
                </p>
              </div>
              <div className="col-span-12 md:col-span-3 self-center flex items-center justify-between md:justify-end gap-4">
                <span className="label group-hover:text-cream">
                  {p.outlet}
                </span>
                <ArrowUpRight
                  size={24}
                  className="transition-transform group-hover:rotate-45"
                />
              </div>
            </motion.article>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-24 md:mt-32 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-6">
            <p className="chapter-num">Press enquiries</p>
            <h3 className="mt-4 font-display text-4xl md:text-5xl leading-[1] tracking-tight">
              Writing about Shruthi? <em className="italic text-terracotta">Let's talk.</em>
            </h3>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 flex flex-col justify-end space-y-3">
            <a
              href="mailto:shruthiravioli@gmail.com?subject=Press%20enquiry"
              className="font-sans text-lg editorial-link inline-block"
              data-testid="press-email"
              data-cursor="hover"
            >
              shruthiravioli@gmail.com
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
